/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import {
  CategoricalScheme,
  ColorScheme,
  ColorSchemeConfig,
  getCategoricalSchemeRegistry,
  getSequentialSchemeRegistry,
  SequentialScheme,
  SequentialSchemeConfig,
  CategoricalAirbnb,
  CategoricalD3,
  CategoricalEcharts,
  CategoricalGoogle,
  CategoricalLyft,
  CategoricalPreset,
  CategoricalSuperset,
  SequentialCommon,
  SequentialD3,
  ColorSchemeRegistry,
} from '@superset-ui/core';

// PriceLabs brand guide: categorical colors are for series and groups; the
// sequential and diverging schemes are for value and comparison shading.
const PriceLabsCategorical = new CategoricalScheme({
  id: 'pricelabsColors',
  label: 'PriceLabs Colors',
  colors: ['#FF4646', '#007B7F', '#003366', '#FFCB1B', '#BFA980', '#FFB4B7', '#7D7D7D', '#263238'],
});
const PriceLabsSequential = new SequentialScheme({
  id: 'pricelabsSequential',
  label: 'PriceLabs Value Shading',
  colors: ['#FFFFFF', '#FACCC8', '#FFAEAD', '#F4777B', '#F5444A'],
});
const PriceLabsDiverging = new SequentialScheme({
  id: 'pricelabsDiverging',
  label: 'PriceLabs Negative / Positive',
  isDiverging: true,
  colors: ['#FF4646', '#FFB4B7', '#FFFFFF', '#9ED6D7', '#007B7F'],
});

function registerColorSchemes<T extends ColorScheme>(
  registry: ColorSchemeRegistry<T>,
  colorSchemes: T[],
  standardDefaultKey: string,
) {
  colorSchemes.forEach(scheme => {
    registry.registerValue(scheme.id, scheme);
  });

  const defaultKey =
    colorSchemes.find(scheme => scheme.isDefault)?.id || standardDefaultKey;
  registry.setDefaultKey(defaultKey);
}

export default function setupColors(
  extraCategoricalColorSchemeConfigs: ColorSchemeConfig[] = [],
  extraSequentialColorSchemeConfigs: SequentialSchemeConfig[] = [],
) {
  const extraCategoricalColorSchemes = extraCategoricalColorSchemeConfigs.map(
    config => new CategoricalScheme(config),
  );
  const extraSequentialColorSchemes = extraSequentialColorSchemeConfigs.map(
    config => new SequentialScheme(config),
  );
  registerColorSchemes(
    // @ts-ignore
    getCategoricalSchemeRegistry(),
    [
      ...CategoricalAirbnb,
      ...CategoricalD3,
      ...CategoricalEcharts,
      ...CategoricalGoogle,
      ...CategoricalLyft,
      ...CategoricalPreset,
      ...CategoricalSuperset,
      PriceLabsCategorical,
      ...extraCategoricalColorSchemes,
    ],
    'supersetColors',
  );
  registerColorSchemes(
    // @ts-ignore
    getSequentialSchemeRegistry(),
    [...SequentialCommon, ...SequentialD3, PriceLabsSequential, PriceLabsDiverging, ...extraSequentialColorSchemes],
    'superset_seq_1',
  );
}
